import { ChartRecommendation, ColumnInfo } from '../../utils/dataAnalyzer';
import { DatasetProfile, EvidenceInsight, Citation } from './types';
import { buildDatasetProfile } from './evidenceProfiler';
import { iqrBounds, linearRegression, parseNumericColumn, pearson } from './statistics';

const confidenceLevel = (score: number): 'high' | 'medium' | 'low' => {
  if (score >= 85) return 'high';
  if (score >= 70) return 'medium';
  return 'low';
};

const makeCitation = (id: string, type: Citation['type'], label: string, detail: string): Citation => ({
  id,
  type,
  label,
  detail
});

export class EvidenceInsightsEngine {
  private rows: any[];
  private headers: string[];
  private columnInfo: Map<string, ColumnInfo>;
  private profile: DatasetProfile;

  constructor(rows: any[], headers: string[], columnInfo: Map<string, ColumnInfo>) {
    this.rows = rows;
    this.headers = headers;
    this.columnInfo = columnInfo;
    this.profile = buildDatasetProfile(rows, headers, columnInfo);
  }

  getProfile(): DatasetProfile {
    return this.profile;
  }

  generateInsights(recommendation: ChartRecommendation): EvidenceInsight[] {
    const candidates: EvidenceInsight[] = [];
    candidates.push(...this.qualityInsights());

    if (recommendation.type === 'line') {
      candidates.push(...this.trendInsights(recommendation));
    }

    if (recommendation.type === 'scatter' || recommendation.type === 'heatmap') {
      candidates.push(...this.correlationInsights(recommendation));
    }

    if (['histogram', 'box', 'violin', 'bar'].includes(recommendation.type)) {
      candidates.push(...this.outlierInsights(recommendation));
    }

    return candidates
      .filter((insight) => insight.confidence >= 70)
      .sort((a, b) => b.confidence - a.confidence)
      .slice(0, 6);
  }

  private qualityInsights(): EvidenceInsight[] {
    const insights: EvidenceInsight[] = [];

    if (this.profile.duplicateRatio >= 0.05) {
      const confidence = Math.min(98, 70 + this.profile.duplicateRatio * 300);
      insights.push({
        id: 'quality-duplicates',
        title: 'Duplicate rows detected',
        finding: `${this.profile.duplicateRows} duplicate rows found (${(this.profile.duplicateRatio * 100).toFixed(1)}%).`,
        confidence,
        confidenceLevel: confidenceLevel(confidence),
        category: 'quality',
        method: 'Exact row-level duplicate detection',
        evidence: [
          { metric: 'Duplicate rows', value: this.profile.duplicateRows },
          { metric: 'Duplicate ratio', value: `${(this.profile.duplicateRatio * 100).toFixed(1)}%`, threshold: '>= 5%' }
        ],
        assumptions: ['Rows are identical after parsing and normalization.'],
        limitations: ['Near-duplicates are not included.'],
        citations: [
          makeCitation('quality-dupes', 'data_quality', 'Duplicate detection', 'Hash-based exact row comparison')
        ],
        recommendation: 'Review duplicate rows before relying on aggregate metrics.'
      });
    }

    return insights;
  }

  private trendInsights(recommendation: ChartRecommendation): EvidenceInsight[] {
    const [xColumn, yColumn] = recommendation.columns;
    const points = this.rows
      .map((row) => ({
        x: new Date(row[xColumn]).getTime(),
        y: Number(row[yColumn])
      }))
      .filter((item) => Number.isFinite(item.x) && Number.isFinite(item.y))
      .sort((a, b) => a.x - b.x);

    if (points.length < 6) return [];

    const xValues = points.map((item) => item.x);
    const yValues = points.map((item) => item.y);
    const { slope, r2 } = linearRegression(xValues, yValues);

    if (Math.abs(slope) < 1e-10 || r2 < 0.35) return [];

    const direction = slope > 0 ? 'upward' : 'downward';
    const confidence = Math.min(96, 70 + r2 * 30);

    return [
      {
        id: `trend-${xColumn}-${yColumn}`,
        title: `${direction.charAt(0).toUpperCase()}${direction.slice(1)} trend in ${yColumn}`,
        finding: `${yColumn} shows a ${direction} trend over ${xColumn} with R² ${r2.toFixed(2)}.`,
        confidence,
        confidenceLevel: confidenceLevel(confidence),
        category: 'trend',
        method: 'Ordinary least squares regression over ordered time points',
        evidence: [
          { metric: 'Data points', value: points.length, threshold: '>= 6' },
          { metric: 'R²', value: r2.toFixed(2), threshold: '>= 0.35' },
          { metric: 'Slope', value: slope.toExponential(3) }
        ],
        assumptions: ['Timestamp parsing is consistent.', 'Linear model approximates trend over selected period.'],
        limitations: ['Trend may not hold outside observed window.', 'Seasonality can reduce linear fit quality.'],
        citations: [
          makeCitation(`trend-col-${xColumn}`, 'column', xColumn, `Time axis used for regression.`),
          makeCitation(`trend-col-${yColumn}`, 'column', yColumn, `Target numeric series used in regression.`),
          makeCitation('trend-window', 'time_window', 'Observed window', `${new Date(points[0].x).toISOString()} to ${new Date(points[points.length - 1].x).toISOString()}`)
        ],
        recommendation: slope > 0
          ? `Validate whether drivers behind growth in ${yColumn} are repeatable.`
          : `Investigate decline drivers and set threshold alerts on ${yColumn}.`
      }
    ];
  }

  private correlationInsights(recommendation: ChartRecommendation): EvidenceInsight[] {
    const numericColumns = recommendation.type === 'heatmap'
      ? recommendation.columns.filter((column) => this.columnInfo.get(column)?.type === 'numeric')
      : recommendation.columns.slice(0, 2);

    if (numericColumns.length < 2) return [];

    const [xColumn, yColumn] = numericColumns;
    const x = parseNumericColumn(this.rows, xColumn);
    const y = parseNumericColumn(this.rows, yColumn);
    const sampleSize = Math.min(x.length, y.length);

    if (sampleSize < 20) return [];

    const correlation = pearson(x.slice(0, sampleSize), y.slice(0, sampleSize));
    const abs = Math.abs(correlation);

    if (abs < 0.5) return [];

    const confidence = Math.min(97, 70 + abs * 27);
    return [
      {
        id: `corr-${xColumn}-${yColumn}`,
        title: `${correlation > 0 ? 'Positive' : 'Negative'} correlation detected`,
        finding: `${xColumn} and ${yColumn} have Pearson correlation ${correlation.toFixed(2)} (n=${sampleSize}).`,
        confidence,
        confidenceLevel: confidenceLevel(confidence),
        category: 'correlation',
        method: 'Pearson correlation coefficient on paired numeric values',
        evidence: [
          { metric: 'Pearson r', value: correlation.toFixed(2), threshold: '|r| >= 0.50' },
          { metric: 'Sample size', value: sampleSize, threshold: '>= 20' }
        ],
        assumptions: ['Numeric pairing is meaningful across rows.'],
        limitations: ['Correlation is not causation.', 'Sensitive to extreme values.'],
        citations: [
          makeCitation(`corr-col-${xColumn}`, 'column', xColumn, 'Numeric predictor column.'),
          makeCitation(`corr-col-${yColumn}`, 'column', yColumn, 'Numeric response column.'),
          makeCitation('corr-aggregate', 'aggregate', 'Correlation aggregate', `Pearson r=${correlation.toFixed(2)}, n=${sampleSize}`)
        ],
        recommendation: 'Use this relationship for monitoring, but validate with segment-level analysis before taking action.'
      }
    ];
  }

  private outlierInsights(recommendation: ChartRecommendation): EvidenceInsight[] {
    const candidateColumns = recommendation.columns.filter((column) => this.columnInfo.get(column)?.type === 'numeric');
    const targetColumn = candidateColumns[0];

    if (!targetColumn) return [];

    const values = parseNumericColumn(this.rows, targetColumn);
    if (values.length < 20) return [];

    const bounds = iqrBounds(values);
    const outlierCount = values.filter((value) => value < bounds.lower || value > bounds.upper).length;
    const ratio = outlierCount / values.length;

    if (ratio < 0.02) return [];

    const confidence = Math.min(95, 72 + ratio * 500);
    return [
      {
        id: `outlier-${targetColumn}`,
        title: `Outliers found in ${targetColumn}`,
        finding: `${outlierCount} outliers detected in ${targetColumn} (${(ratio * 100).toFixed(1)}% of values).`,
        confidence,
        confidenceLevel: confidenceLevel(confidence),
        category: 'outlier',
        method: 'IQR method using 1.5×IQR fences',
        evidence: [
          { metric: 'Outlier count', value: outlierCount },
          { metric: 'Outlier ratio', value: `${(ratio * 100).toFixed(1)}%`, threshold: '>= 2%' },
          { metric: 'Lower bound', value: bounds.lower.toFixed(2) },
          { metric: 'Upper bound', value: bounds.upper.toFixed(2) }
        ],
        assumptions: ['Distribution is reasonably stable across rows.'],
        limitations: ['Seasonal spikes may be misclassified as outliers.'],
        citations: [
          makeCitation(`outlier-col-${targetColumn}`, 'column', targetColumn, 'Numeric column analyzed with IQR fences.'),
          makeCitation('outlier-filter', 'filter', 'IQR filter', `Values < ${bounds.lower.toFixed(2)} or > ${bounds.upper.toFixed(2)}.`)
        ],
        recommendation: 'Review flagged rows and decide whether to cap, exclude, or investigate upstream causes.'
      }
    ];
  }
}
